import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import ImageUploader from '../components/ImageUploader';
import { Settings, Image, Layout, Search, FileText, BarChart3, Save, RefreshCw, Eye, Users, Sparkles, Truck, Upload, Copy, Trash2, Check, Megaphone, DollarSign, Hash, FolderKanban, Mail, AlertTriangle, ImageOff, Star } from 'lucide-react';
import WelcomeCampaignPanel from '../components/WelcomeCampaignPanel';
import AdminUsersPanel from '../components/AdminUsersPanel';
import WpImportPanel from '../components/WpImportPanel';
import UncategorizedProductsPanel from '../components/UncategorizedProductsPanel';
import ProductsWithoutImagePanel from '../components/ProductsWithoutImagePanel';
import AdminReviewsPanel from '../components/AdminReviewsPanel';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminPage = () => {
  const { t } = useTranslation();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [stats, setStats] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [quotesTotal, setQuotesTotal] = useState(0);
  const [users, setUsers] = useState([]);
  const [recStatus, setRecStatus] = useState(null);
  const [computing, setComputing] = useState(false);
  const [clearingCache, setClearingCache] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  // Upload state
  const [uploads, setUploads] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(null);
  const fileInputRef = useRef(null);
  const [activeTab, setActiveTab] = useState('hero');
  const [audit, setAudit] = useState({ uncategorized: null, withoutImage: null });
  const [pristine, setPristine] = useState(null); // last server-confirmed snapshot
  const autoSaveTimer = useRef(null);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) navigate('/connexion');
    else if (user && user.role !== 'admin') navigate('/');
  }, [authLoading, isAuthenticated, user, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [settingsRes, statsRes, quotesRes, usersRes, recStatusRes, uploadsRes, uncatRes, noImgRes] = await Promise.all([
          fetch(`${API_BASE}/admin/settings`).then(r => r.json()),
          fetch(`${API_BASE}/admin/stats`, { credentials: 'include' }).then(r => r.json()).catch(() => null),
          fetch(`${API_BASE}/admin/quotes?limit=10`, { credentials: 'include' }).then(r => r.json()).catch(() => ({ quotes: [], total: 0 })),
          fetch(`${API_BASE}/admin/users`, { credentials: 'include' }).then(r => r.json()).catch(() => ({ users: [] })),
          fetch(`${API_BASE}/admin/recommendations/status`, { credentials: 'include' }).then(r => r.json()).catch(() => null),
          fetch(`${API_BASE}/upload/list`, { credentials: 'include' }).then(r => r.json()).catch(() => ({ uploads: [] })),
          fetch(`${API_BASE}/admin/products/uncategorized`, { credentials: 'include' }).then(r => r.json()).catch(() => null),
          fetch(`${API_BASE}/admin/products/without-image`, { credentials: 'include' }).then(r => r.json()).catch(() => null),
        ]);
        setSettings(settingsRes);
        setPristine(settingsRes); // baseline for dirty-tracking
        if (statsRes) setStats(statsRes);
        setQuotes(quotesRes.quotes || []);
        setQuotesTotal(quotesRes.total || 0);
        setUsers(usersRes.users || []);
        setRecStatus(recStatusRes);
        setUploads(uploadsRes.uploads || []);
        setAudit({
          uncategorized: uncatRes?.uncategorized_count ?? null,
          withoutImage: noImgRes?.without_image_count ?? null,
        });
      } catch (err) {
        console.error('Failed to fetch admin data:', err);
      } finally {
        setLoading(false);
      }
    };
    if (isAuthenticated && user?.role === 'admin') fetchData();
  }, [isAuthenticated, user]);

  const saveSection = async (section, data) => {
    setSaving(true);
    setSaveMessage('');
    try {
      const res = await fetch(`${API_BASE}/admin/settings/${section}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      const result = await res.json();
      if (res.ok) {
        setSettings(result.settings);
        setPristine(result.settings); // server-confirmed → reset baseline
        setSaveMessage(t('admin.saved'));
        setTimeout(() => setSaveMessage(''), 3000);
      }
    } catch { setSaveMessage(t('admin.save_error')); }
    finally { setSaving(false); }
  };

  // Debounced auto-save for the Hero section — saves 2.5s after the last edit
  // so admins never lose carousel images after uploading.
  useEffect(() => {
    if (!settings || !pristine) return;
    const heroDirty = JSON.stringify(settings.hero) !== JSON.stringify(pristine.hero);
    if (!heroDirty) return;
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      saveSection('hero', settings.hero);
    }, 2500);
    return () => { if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current); };
  }, [settings?.hero, pristine?.hero]);

  const heroDirty = !!(settings && pristine && JSON.stringify(settings.hero) !== JSON.stringify(pristine.hero));

  const updateQuoteStatus = async (quoteId, status) => {
    try {
      await fetch(`${API_BASE}/admin/quotes/${quoteId}/status`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      setQuotes(prev => prev.map(q => q.id === quoteId ? { ...q, status } : q));
    } catch (err) { console.error('Failed to update quote:', err); }
  };

  const updateUserRole = async (userId, role) => {
    try {
      await fetch(`${API_BASE}/admin/users/${userId}/role`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role })
      });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, role } : u));
    } catch (err) { console.error('Failed to update role:', err); }
  };

  const triggerRecommendations = async () => {
    setComputing(true);
    try {
      const res = await fetch(`${API_BASE}/admin/recommendations/compute`, { method: 'POST', credentials: 'include' });
      const result = await res.json();
      setRecStatus(result);
      setSaveMessage(t('admin.recs_done'));
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (err) { console.error('Failed:', err); }
    finally { setComputing(false); }
  };

  // Upload handlers
  const handleClearCache = async () => {
    setClearingCache(true);
    try {
      const res = await fetch(`${API_BASE}/admin/clear-cache`, { method: 'POST', credentials: 'include' });
      const result = await res.json();
      setSaveMessage(result.message || 'Cache vidé');
      setTimeout(() => setSaveMessage(''), 4000);
    } catch (err) { console.error('Cache clear failed:', err); }
    finally { setClearingCache(false); }
  };

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    setUploading(true);
    try {
      const formData = new FormData();
      for (const f of files) formData.append('files', f);
      const res = await fetch(`${API_BASE}/upload/images`, { method: 'POST', credentials: 'include', body: formData });
      const result = await res.json();
      if (result.uploads) {
        const newUploads = result.uploads.filter(u => u.url).map(u => ({ ...u, created_at: new Date().toISOString() }));
        setUploads(prev => [...newUploads, ...prev]);
      }
    } catch (err) { console.error('Upload failed:', err); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const handleDeleteUpload = async (filename) => {
    await fetch(`${API_BASE}/upload/${filename}`, { method: 'DELETE', credentials: 'include' });
    setUploads(prev => prev.filter(u => u.filename !== filename));
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url);
    setCopiedUrl(url);
    setTimeout(() => setCopiedUrl(null), 2000);
  };

  const updateHero = (key, value) => setSettings(prev => ({ ...prev, hero: { ...prev.hero, [key]: value } }));
  const updateAppearance = (key, value) => setSettings(prev => ({ ...prev, appearance: { ...prev.appearance, [key]: value } }));
  const updateCatalogue = (key, value) => setSettings(prev => ({ ...prev, catalogue: { ...prev.catalogue, [key]: value } }));
  const updatePopup = (key, value) => setSettings(prev => ({ ...prev, popup: { ...(prev.popup || {}), [key]: value } }));
  const updateDelivery = (key, value) => setSettings(prev => ({ ...prev, delivery: { ...(prev.delivery || {}), [key]: value } }));
  const updateDeliveryZone = (i, field, value) => {
    setSettings(prev => {
      const zones = [...(prev.delivery?.zones || [])];
      zones[i] = { ...zones[i], [field]: field === 'surcharge' ? Number(value) : value };
      return { ...prev, delivery: { ...prev.delivery, zones } };
    });
  };
  const addDeliveryZone = () => setSettings(prev => ({ ...prev, delivery: { ...prev.delivery, zones: [...(prev.delivery?.zones || []), { name: '', color: '#000000', surcharge: 0 }] } }));
  const removeDeliveryZone = (i) => setSettings(prev => ({ ...prev, delivery: { ...prev.delivery, zones: (prev.delivery?.zones || []).filter((_, idx) => idx !== i) } }));
  const updateDeliveryTimeSlot = (i, field, value) => {
    setSettings(prev => {
      const slots = [...(prev.delivery?.time_slots || [])];
      slots[i] = { ...slots[i], [field]: field === 'surcharge' ? Number(value) : value };
      return { ...prev, delivery: { ...prev.delivery, time_slots: slots } };
    });
  };
  const addDeliveryTimeSlot = () => setSettings(prev => ({ ...prev, delivery: { ...prev.delivery, time_slots: [...(prev.delivery?.time_slots || []), { label: '', surcharge: 0 }] } }));
  const removeDeliveryTimeSlot = (i) => setSettings(prev => ({ ...prev, delivery: { ...prev.delivery, time_slots: (prev.delivery?.time_slots || []).filter((_, idx) => idx !== i) } }));
  const updateDeliveryType = (i, field, value) => {
    setSettings(prev => {
      const types = [...(prev.delivery?.delivery_types || [])];
      types[i] = { ...types[i], [field]: field === 'surcharge' ? Number(value) : value };
      return { ...prev, delivery: { ...prev.delivery, delivery_types: types } };
    });
  };
  const addDeliveryType = () => setSettings(prev => ({ ...prev, delivery: { ...prev.delivery, delivery_types: [...(prev.delivery?.delivery_types || []), { label: '', surcharge: 0 }] } }));
  const removeDeliveryType = (i) => setSettings(prev => ({ ...prev, delivery: { ...prev.delivery, delivery_types: (prev.delivery?.delivery_types || []).filter((_, idx) => idx !== i) } }));

  if (loading || !settings) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="admin-loading">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const statusLabels = { pending: t('admin.status_pending'), reviewing: t('admin.status_reviewing'), accepted: t('admin.status_accepted'), rejected: t('admin.status_rejected'), completed: t('admin.status_completed') };
  const statusColors = { pending: 'bg-yellow-100 text-yellow-800', reviewing: 'bg-blue-100 text-blue-800', accepted: 'bg-green-100 text-green-800', rejected: 'bg-red-100 text-red-800', completed: 'bg-gray-100 text-gray-800' };

  return (
    <div className="min-h-screen bg-gray-50 py-8" data-testid="admin-page">
      <div className="container mx-auto px-4 md:px-8 lg:px-16">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground mb-1">{t('admin.title')}</p>
            <h1 className="text-2xl font-extrabold tracking-tight">{t('admin.panel_title')}</h1>
          </div>
          <div className="flex items-center gap-3">
            {saveMessage && <span className="text-sm text-green-600 font-medium" data-testid="save-message">{saveMessage}</span>}
            <Button
              variant="outline"
              size="sm"
              className="rounded-none gap-2"
              onClick={handleClearCache}
              disabled={clearingCache}
              data-testid="clear-cache-btn"
            >
              <RefreshCw className={`w-4 h-4 ${clearingCache ? 'animate-spin' : ''}`} />
              {clearingCache ? 'Synchronisation...' : 'Sync back-office'}
            </Button>
            <a href="/" target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="sm" className="rounded-none gap-2"><Eye className="w-4 h-4" /> {t('admin.view_site')}</Button>
            </a>
          </div>
        </div>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
            <Card className="rounded-none"><CardContent className="p-3 flex items-center gap-3"><div className="w-9 h-9 bg-blue-100 flex items-center justify-center flex-shrink-0"><FileText className="w-4 h-4 text-blue-600" /></div><div><p className="text-xl font-bold">{stats.total_quotes}</p><p className="text-[10px] text-muted-foreground">{t('admin.quotes_label')}</p></div></CardContent></Card>
            <button type="button" onClick={() => setActiveTab('without-image')} className="text-left hover:ring-2 hover:ring-rose-300 transition" data-testid="stat-card-without-image">
              <Card className="rounded-none cursor-pointer h-full"><CardContent className="p-3 flex items-center gap-3"><div className="w-9 h-9 bg-rose-100 flex items-center justify-center flex-shrink-0"><ImageOff className="w-4 h-4 text-rose-600" /></div><div><p className="text-xl font-bold">{audit.withoutImage ?? '—'}</p><p className="text-[10px] text-muted-foreground">Produits sans photo</p></div></CardContent></Card>
            </button>
            <button type="button" onClick={() => setActiveTab('uncategorized')} className="text-left hover:ring-2 hover:ring-orange-300 transition" data-testid="stat-card-uncategorized">
              <Card className="rounded-none cursor-pointer h-full"><CardContent className="p-3 flex items-center gap-3"><div className="w-9 h-9 bg-orange-100 flex items-center justify-center flex-shrink-0"><AlertTriangle className="w-4 h-4 text-orange-600" /></div><div><p className="text-xl font-bold">{audit.uncategorized ?? '—'}</p><p className="text-[10px] text-muted-foreground">Produits sans catégorie</p></div></CardContent></Card>
            </button>
            <Card className="rounded-none"><CardContent className="p-3 flex items-center gap-3"><div className="w-9 h-9 bg-purple-100 flex items-center justify-center flex-shrink-0"><Users className="w-4 h-4 text-purple-600" /></div><div><p className="text-xl font-bold">{stats.total_users}</p><p className="text-[10px] text-muted-foreground">{t('admin.users_label')}</p></div></CardContent></Card>
            <Card className="rounded-none"><CardContent className="p-3 flex items-center gap-3"><div className="w-9 h-9 bg-cyan-100 flex items-center justify-center flex-shrink-0"><Hash className="w-4 h-4 text-cyan-600" /></div><div><p className="text-xl font-bold">{stats.total_cpus || 0}</p><p className="text-[10px] text-muted-foreground">{t('admin.cpus_label')}</p></div></CardContent></Card>
            <Card className="rounded-none"><CardContent className="p-3 flex items-center gap-3"><div className="w-9 h-9 bg-orange-100 flex items-center justify-center flex-shrink-0"><FolderKanban className="w-4 h-4 text-orange-600" /></div><div><p className="text-xl font-bold">{stats.recent_quotes_7d || 0}</p><p className="text-[10px] text-muted-foreground">{t('admin.recent_7d_label')}</p></div></CardContent></Card>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border rounded-none h-auto flex-wrap">
            <TabsTrigger value="hero" className="rounded-none gap-2 data-[state=active]:bg-primary data-[state=active]:text-white"><Layout className="w-4 h-4" /> {t('admin.tab_hero')}</TabsTrigger>
            <TabsTrigger value="appearance" className="rounded-none gap-2 data-[state=active]:bg-primary data-[state=active]:text-white"><Image className="w-4 h-4" /> {t('admin.tab_appearance')}</TabsTrigger>
            <TabsTrigger value="catalogue" className="rounded-none gap-2 data-[state=active]:bg-primary data-[state=active]:text-white"><Search className="w-4 h-4" /> {t('admin.tab_catalogue')}</TabsTrigger>
            <TabsTrigger value="quotes" className="rounded-none gap-2 data-[state=active]:bg-primary data-[state=active]:text-white"><FileText className="w-4 h-4" /> {t('admin.tab_quotes')} ({quotesTotal})</TabsTrigger>
            <TabsTrigger value="users" className="rounded-none gap-2 data-[state=active]:bg-primary data-[state=active]:text-white"><Users className="w-4 h-4" /> {t('admin.tab_users')}</TabsTrigger>
            <TabsTrigger value="recommendations" className="rounded-none gap-2 data-[state=active]:bg-primary data-[state=active]:text-white"><Sparkles className="w-4 h-4" /> {t('admin.tab_recommendations')}</TabsTrigger>
            <TabsTrigger value="delivery" className="rounded-none gap-2 data-[state=active]:bg-primary data-[state=active]:text-white"><Truck className="w-4 h-4" /> {t('admin.tab_delivery')}</TabsTrigger>
            <TabsTrigger value="uploads" className="rounded-none gap-2 data-[state=active]:bg-primary data-[state=active]:text-white"><Upload className="w-4 h-4" /> {t('admin.tab_uploads')}</TabsTrigger>
            <TabsTrigger value="popup" className="rounded-none gap-2 data-[state=active]:bg-primary data-[state=active]:text-white"><Megaphone className="w-4 h-4" /> {t('admin.tab_popup')}</TabsTrigger>
            <TabsTrigger value="campaign" className="rounded-none gap-2 data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="tab-campaign"><Mail className="w-4 h-4" /> Campagne WP</TabsTrigger>
            <TabsTrigger value="wp-import" className="rounded-none gap-2 data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="tab-wp-import"><Upload className="w-4 h-4" /> Import WP</TabsTrigger>
            <TabsTrigger value="uncategorized" className="rounded-none gap-2 data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="tab-uncategorized"><AlertTriangle className="w-4 h-4" /> Produits sans catégorie</TabsTrigger>
            <TabsTrigger value="without-image" className="rounded-none gap-2 data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="tab-without-image"><ImageOff className="w-4 h-4" /> Produits sans photo</TabsTrigger>
            <TabsTrigger value="reviews" className="rounded-none gap-2 data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="tab-reviews"><Star className="w-4 h-4" /> Avis clients</TabsTrigger>
          </TabsList>

          {/* HERO */}
          <TabsContent value="hero">
            <Card className="rounded-none">
              <CardHeader><CardTitle className="text-lg">{t('admin.hero_config')}</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                {/* Unsaved-changes banner with auto-save countdown */}
                {heroDirty && (
                  <div className="sticky top-0 z-10 flex items-center justify-between gap-3 bg-amber-50 border border-amber-300 px-3 py-2 text-sm" data-testid="hero-unsaved-banner">
                    <div className="flex items-center gap-2 text-amber-900">
                      <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                      <span>Modifications non sauvegardées — {saving ? 'enregistrement en cours…' : 'sauvegarde auto dans 2,5 s'}</span>
                    </div>
                    <Button size="sm" className="rounded-none h-7 text-xs" disabled={saving} onClick={() => saveSection('hero', settings.hero)} data-testid="hero-save-now-btn">
                      <Save className="w-3.5 h-3.5 mr-1" /> Enregistrer maintenant
                    </Button>
                  </div>
                )}
                <div className="flex items-center justify-between"><Label>{t('admin.hero_enabled')}</Label><Switch checked={settings.hero.enabled} onCheckedChange={(v) => updateHero('enabled', v)} data-testid="hero-enabled-switch" /></div>
                <Separator />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>{t('admin.hero_title')}</Label><Input value={settings.hero.title} onChange={(e) => updateHero('title', e.target.value)} className="rounded-none" data-testid="hero-title-input" /></div>
                  <div className="space-y-2">
                    <Label>{t('admin.hero_bg')}</Label>
                    <ImageUploader value={settings.hero.background_image} onChange={(url) => updateHero('background_image', url)} testIdPrefix="hero-bg" />
                  </div>
                </div>
                <div className="space-y-2"><Label>{t('admin.hero_subtitle')}</Label><Textarea value={settings.hero.subtitle} onChange={(e) => updateHero('subtitle', e.target.value)} className="rounded-none" data-testid="hero-subtitle-input" /></div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>{t('admin.hero_btn_primary_text')}</Label><Input value={settings.hero.button_primary_text} onChange={(e) => updateHero('button_primary_text', e.target.value)} className="rounded-none" /></div>
                  <div className="space-y-2"><Label>{t('admin.hero_btn_primary_link')}</Label><Input value={settings.hero.button_primary_link} onChange={(e) => updateHero('button_primary_link', e.target.value)} className="rounded-none" /></div>
                  <div className="space-y-2"><Label>{t('admin.hero_btn_secondary_text')}</Label><Input value={settings.hero.button_secondary_text} onChange={(e) => updateHero('button_secondary_text', e.target.value)} className="rounded-none" /></div>
                  <div className="space-y-2"><Label>{t('admin.hero_btn_secondary_link')}</Label><Input value={settings.hero.button_secondary_link} onChange={(e) => updateHero('button_secondary_link', e.target.value)} className="rounded-none" /></div>
                </div>
                {settings.hero.background_image && (
                  <div className="mt-4"><Label className="mb-2 block">{t('admin.hero_preview')}</Label><div className="relative h-48 bg-cover bg-center overflow-hidden" style={{ backgroundImage: `url(${settings.hero.background_image})` }}><div className="absolute inset-0 bg-black/50 flex items-center p-8"><div className="text-white"><h2 className="text-xl font-bold">{settings.hero.title}</h2><p className="text-sm opacity-80 mt-1 line-clamp-2">{settings.hero.subtitle}</p></div></div></div></div>
                )}
                
                {/* Carousel Settings */}
                <Separator />
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label>Carrousel d'images</Label>
                    <Switch checked={settings.hero.carousel_enabled || false} onCheckedChange={(v) => updateHero('carousel_enabled', v)} data-testid="carousel-enabled-switch" />
                  </div>
                  {settings.hero.carousel_enabled && (
                    <>
                      <div className="space-y-2">
                        <Label>Durée d'affichage (ms)</Label>
                        <Input type="number" min="1000" step="500" value={settings.hero.carousel_duration || 5000} onChange={(e) => updateHero('carousel_duration', parseInt(e.target.value) || 5000)} className="rounded-none w-40" data-testid="carousel-duration-input" />
                      </div>
                      <div className="space-y-2">
                        <Label>Images du carrousel</Label>
                        {(settings.hero.carousel_images || []).map((img, idx) => (
                          <div key={idx} className="border border-border p-2 space-y-2">
                            <div className="flex items-start gap-2">
                              <span className="text-xs text-muted-foreground w-6 text-center mt-2">{idx + 1}</span>
                              <div className="flex-1 min-w-0">
                                <ImageUploader value={img.url || ''} onChange={(url) => {
                                  const updated = [...(settings.hero.carousel_images || [])];
                                  updated[idx] = { ...updated[idx], url };
                                  updateHero('carousel_images', updated);
                                }} testIdPrefix={`carousel-img-${idx}`} />
                              </div>
                              <div className="flex flex-col gap-1">
                                <Button variant="ghost" size="sm" disabled={idx === 0} onClick={() => {
                                  const updated = [...(settings.hero.carousel_images || [])];
                                  [updated[idx - 1], updated[idx]] = [updated[idx], updated[idx - 1]];
                                  updateHero('carousel_images', updated);
                                }} className="px-2">&#8593;</Button>
                                <Button variant="ghost" size="sm" disabled={idx === (settings.hero.carousel_images || []).length - 1} onClick={() => {
                                  const updated = [...(settings.hero.carousel_images || [])];
                                  [updated[idx], updated[idx + 1]] = [updated[idx + 1], updated[idx]];
                                  updateHero('carousel_images', updated);
                                }} className="px-2">&#8595;</Button>
                                <Button variant="ghost" size="sm" onClick={() => {
                                  const updated = (settings.hero.carousel_images || []).filter((_, i) => i !== idx);
                                  updateHero('carousel_images', updated);
                                }} className="px-2 text-red-500 hover:text-red-700"><Trash2 className="w-3.5 h-3.5" /></Button>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 ml-8">
                              <Input value={img.link_url || ''} onChange={(e) => {
                                const updated = [...(settings.hero.carousel_images || [])];
                                updated[idx] = { ...updated[idx], link_url: e.target.value };
                                updateHero('carousel_images', updated);
                              }} placeholder="Lien cliquable (ex: /catalogue?nav_menu=IMAGE)" className="rounded-none flex-1 text-xs h-8" data-testid={`carousel-img-link-${idx}`} />
                            </div>
                          </div>
                        ))}
                        <Button variant="outline" size="sm" className="rounded-none text-xs" onClick={() => {
                          const updated = [...(settings.hero.carousel_images || []), { url: '', link_url: '' }];
                          updateHero('carousel_images', updated);
                        }} data-testid="add-carousel-image-btn">
                          + Ajouter une image
                        </Button>
                      </div>
                    </>
                  )}
                </div>
                
                <Button onClick={() => saveSection('hero', settings.hero)} disabled={saving} className="rounded-none gap-2" data-testid="save-hero-btn"><Save className="w-4 h-4" /> {saving ? t('admin.saving') : t('admin.hero_save')}</Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* APPEARANCE */}
          <TabsContent value="appearance">
            <Card className="rounded-none">
              <CardHeader><CardTitle className="text-lg">{t('admin.appearance_title')}</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('admin.logo_url')}</Label>
                    <ImageUploader value={settings.appearance.logo_url} onChange={(url) => updateAppearance('logo_url', url)} testIdPrefix="appearance-logo" />
                  </div>
                  <div className="space-y-2">
                    <Label>Favicon (icône onglet navigateur)</Label>
                    <ImageUploader value={settings.appearance.favicon_url || ''} onChange={(url) => updateAppearance('favicon_url', url)} accept="image/png,image/x-icon,image/svg+xml,image/jpeg" testIdPrefix="appearance-favicon" />
                  </div>
                </div>
                <div className="space-y-2"><Label>{t('admin.primary_color')}</Label><div className="flex items-center gap-3"><input type="color" value={settings.appearance.primary_color} onChange={(e) => updateAppearance('primary_color', e.target.value)} className="w-12 h-10 cursor-pointer border rounded" data-testid="appearance-color-input" /><Input value={settings.appearance.primary_color} onChange={(e) => updateAppearance('primary_color', e.target.value)} className="rounded-none flex-1" /></div></div>
                <Separator />
                <h3 className="font-semibold text-sm">{t('admin.footer_info')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>{t('admin.footer_phone')}</Label><Input value={settings.appearance.footer_phone} onChange={(e) => updateAppearance('footer_phone', e.target.value)} className="rounded-none" /></div>
                  <div className="space-y-2"><Label>{t('admin.footer_email')}</Label><Input value={settings.appearance.footer_email} onChange={(e) => updateAppearance('footer_email', e.target.value)} className="rounded-none" /></div>
                  <div className="space-y-2"><Label>{t('admin.footer_address')}</Label><Input value={settings.appearance.footer_address} onChange={(e) => updateAppearance('footer_address', e.target.value)} className="rounded-none" /></div>
                  <div className="space-y-2"><Label>{t('admin.footer_hours')}</Label><Input value={settings.appearance.footer_hours} onChange={(e) => updateAppearance('footer_hours', e.target.value)} className="rounded-none" /></div>
                </div>
                <Button onClick={() => saveSection('appearance', settings.appearance)} disabled={saving} className="rounded-none gap-2" data-testid="save-appearance-btn"><Save className="w-4 h-4" /> {saving ? t('admin.saving') : t('admin.appearance_save')}</Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* CATALOGUE */}
          <TabsContent value="catalogue">
            <Card className="rounded-none">
              <CardHeader><CardTitle className="text-lg">{t('admin.catalogue_config')}</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>{t('admin.products_per_page')}</Label><Select value={String(settings.catalogue.products_per_page)} onValueChange={(v) => updateCatalogue('products_per_page', parseInt(v))}><SelectTrigger className="rounded-none" data-testid="catalogue-per-page"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="12">12</SelectItem><SelectItem value="20">20</SelectItem><SelectItem value="36">36</SelectItem><SelectItem value="48">48</SelectItem></SelectContent></Select></div>
                  <div className="space-y-2"><Label>{t('admin.home_products')}</Label><Select value={String(settings.catalogue.home_products_count)} onValueChange={(v) => updateCatalogue('home_products_count', parseInt(v))}><SelectTrigger className="rounded-none" data-testid="catalogue-home-count"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="3">3</SelectItem><SelectItem value="6">6</SelectItem><SelectItem value="9">9</SelectItem><SelectItem value="12">12</SelectItem></SelectContent></Select></div>
                </div>
                <Button onClick={() => saveSection('catalogue', settings.catalogue)} disabled={saving} className="rounded-none gap-2" data-testid="save-catalogue-btn"><Save className="w-4 h-4" /> {saving ? t('admin.saving') : t('admin.catalogue_save')}</Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* QUOTES */}
          <TabsContent value="quotes">
            <Card className="rounded-none">
              <CardHeader><CardTitle className="text-lg">{t('admin.quotes_label')} ({quotesTotal})</CardTitle></CardHeader>
              <CardContent>
                {quotes.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">{t('admin.no_quotes')}</p>
                ) : (
                  <div className="space-y-3">
                    {quotes.map((quote) => (
                      <div key={quote.id} className="border border-border p-4 flex flex-col md:flex-row md:items-center justify-between gap-3" data-testid={`quote-${quote.id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-xs px-2 py-0.5 font-medium ${statusColors[quote.status] || 'bg-gray-100'}`}>{statusLabels[quote.status] || quote.status}</span>
                            <span className="text-xs text-muted-foreground">#{quote.id?.slice(-6)}</span>
                          </div>
                          <p className="text-sm font-medium">{quote.user_name || quote.user_email || 'Client'}</p>
                          <p className="text-xs text-muted-foreground">{quote.items?.length || 0} {t('admin.articles_count')} · {quote.total?.toFixed(2)} € · {quote.pickup_date} → {quote.return_date}</p>
                          {quote.message && <p className="text-xs text-muted-foreground mt-1 truncate">"{quote.message}"</p>}
                        </div>
                        <Select value={quote.status} onValueChange={(v) => updateQuoteStatus(quote.id, v)}>
                          <SelectTrigger className="rounded-none w-[140px] h-8 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">{t('admin.status_pending')}</SelectItem>
                            <SelectItem value="reviewing">{t('admin.status_reviewing')}</SelectItem>
                            <SelectItem value="accepted">{t('admin.status_accepted')}</SelectItem>
                            <SelectItem value="rejected">{t('admin.status_rejected')}</SelectItem>
                            <SelectItem value="completed">{t('admin.status_completed')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* USERS */}
          <TabsContent value="users">
            <AdminUsersPanel />
          </TabsContent>

          {/* RECOMMENDATIONS */}
          <TabsContent value="recommendations">
            <Card className="rounded-none">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">{t('admin.recs_title')}</CardTitle>
                  <Button onClick={triggerRecommendations} disabled={computing} className="rounded-none gap-2" data-testid="compute-recs-btn"><RefreshCw className={`w-4 h-4 ${computing ? 'animate-spin' : ''}`} /> {computing ? t('admin.recs_computing') : t('admin.recs_compute')}</Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">{t('admin.recs_desc')}</p>
                {recStatus && (
                  <div className="bg-gray-50 p-4 space-y-2">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div><p className="text-muted-foreground">{t('admin.recs_status')}</p><p className="font-semibold">{recStatus.status === 'computed' ? t('admin.recs_computed') : t('admin.recs_never')}</p></div>
                      <div><p className="text-muted-foreground">{t('admin.recs_quotes_analyzed')}</p><p className="font-semibold">{recStatus.quotes_analyzed || 0}</p></div>
                      <div><p className="text-muted-foreground">{t('admin.recs_products_tracked')}</p><p className="font-semibold">{recStatus.products_tracked || 0}</p></div>
                      <div><p className="text-muted-foreground">{t('admin.recs_last_computed')}</p><p className="font-semibold">{recStatus.computed_at ? new Date(recStatus.computed_at).toLocaleString('fr-FR') : t('admin.recs_never')}</p></div>
                    </div>
                  </div>
                )}
                <Separator />
                <h3 className="font-semibold text-sm">{t('admin.recs_logic_title')}</h3>
                <div className="space-y-2 text-sm text-muted-foreground">
                  <p><strong>1.</strong> {t('admin.recs_logic_1')}</p>
                  <p><strong>2.</strong> {t('admin.recs_logic_2')}</p>
                  <p><strong>3.</strong> {t('admin.recs_logic_3')}</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* DELIVERY */}
          <TabsContent value="delivery">
            <Card className="rounded-none">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">{t('admin.delivery_config_title')}</CardTitle>
                  <Button onClick={() => saveSection('delivery', settings.delivery)} disabled={saving} className="rounded-none gap-2" data-testid="save-delivery-btn"><Save className="w-4 h-4" /> {saving ? t('admin.saving') : t('admin.save')}</Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between"><div><Label className="font-semibold">{t('admin.delivery_enable')}</Label><p className="text-xs text-muted-foreground mt-1">{t('admin.delivery_enable_desc')}</p></div><Switch checked={settings.delivery?.enabled || false} onCheckedChange={(v) => updateDelivery('enabled', v)} data-testid="delivery-enabled-switch" /></div>
                <Separator />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2"><Label>{t('admin.delivery_threshold')}</Label><Input type="number" value={settings.delivery?.threshold || 100} onChange={(e) => updateDelivery('threshold', Number(e.target.value))} className="rounded-none" data-testid="delivery-threshold" /><p className="text-xs text-muted-foreground">{t('admin.delivery_threshold_desc')}</p></div>
                  <div className="space-y-2"><Label>{t('admin.delivery_base_price')}</Label><Input type="number" value={settings.delivery?.base_price || 65} onChange={(e) => updateDelivery('base_price', Number(e.target.value))} className="rounded-none" data-testid="delivery-base-price" /></div>
                  <div className="space-y-2"><Label>{t('admin.delivery_urgency')}</Label><Input type="number" value={settings.delivery?.urgency_surcharge || 50} onChange={(e) => updateDelivery('urgency_surcharge', Number(e.target.value))} className="rounded-none" /></div>
                </div>
                <Separator />
                <div>
                  <div className="flex items-center justify-between mb-3"><div><Label className="font-semibold">{t('admin.delivery_zones')}</Label><p className="text-xs text-muted-foreground mt-1">{t('admin.delivery_zones_desc')}</p></div><Button variant="outline" size="sm" onClick={addDeliveryZone} className="rounded-none" data-testid="add-zone-btn">+ {t('admin.delivery_add')}</Button></div>
                  <div className="space-y-2">{(settings.delivery?.zones || []).map((z, i) => (<div key={i} className="flex items-center gap-2"><input type="color" value={z.color || '#000000'} onChange={(e) => updateDeliveryZone(i, 'color', e.target.value)} className="w-8 h-8 cursor-pointer border border-border" /><Input placeholder="Nom" value={z.name} onChange={(e) => updateDeliveryZone(i, 'name', e.target.value)} className="rounded-none flex-1" /><Input type="number" value={z.surcharge} onChange={(e) => updateDeliveryZone(i, 'surcharge', e.target.value)} className="rounded-none w-24" /><span className="text-xs text-muted-foreground whitespace-nowrap">EUR</span><Button variant="ghost" size="sm" onClick={() => removeDeliveryZone(i)} className="text-red-500 hover:text-red-700 px-2">&times;</Button></div>))}</div>
                </div>
                <Separator />
                <div>
                  <div className="flex items-center justify-between mb-3"><Label className="font-semibold">{t('admin.delivery_types')}</Label><Button variant="outline" size="sm" onClick={addDeliveryType} className="rounded-none" data-testid="add-delivery-type-btn">+ {t('admin.delivery_add')}</Button></div>
                  <div className="space-y-2">{(settings.delivery?.delivery_types || []).map((dt, i) => (<div key={i} className="flex items-center gap-2"><Input placeholder="Label" value={dt.label} onChange={(e) => updateDeliveryType(i, 'label', e.target.value)} className="rounded-none flex-1" /><Input type="number" value={dt.surcharge} onChange={(e) => updateDeliveryType(i, 'surcharge', e.target.value)} className="rounded-none w-24" /><span className="text-xs text-muted-foreground whitespace-nowrap">EUR</span><Button variant="ghost" size="sm" onClick={() => removeDeliveryType(i)} className="text-red-500 hover:text-red-700 px-2">&times;</Button></div>))}</div>
                </div>
                <Separator />
                <div>
                  <div className="flex items-center justify-between mb-3"><Label className="font-semibold">{t('admin.delivery_timeslots')}</Label><Button variant="outline" size="sm" onClick={addDeliveryTimeSlot} className="rounded-none" data-testid="add-timeslot-btn">+ {t('admin.delivery_add')}</Button></div>
                  <div className="space-y-2">{(settings.delivery?.time_slots || []).map((slot, i) => (<div key={i} className="flex items-center gap-2"><Input placeholder="Label" value={slot.label} onChange={(e) => updateDeliveryTimeSlot(i, 'label', e.target.value)} className="rounded-none flex-1" /><Input type="number" value={slot.surcharge} onChange={(e) => updateDeliveryTimeSlot(i, 'surcharge', e.target.value)} className="rounded-none w-24" /><span className="text-xs text-muted-foreground whitespace-nowrap">EUR</span><Button variant="ghost" size="sm" onClick={() => removeDeliveryTimeSlot(i)} className="text-red-500 hover:text-red-700 px-2">&times;</Button></div>))}</div>
                </div>
                <Separator />
                <div><Label className="font-semibold">{t('admin.delivery_vehicle_skus')}</Label><p className="text-xs text-muted-foreground mt-1 mb-2">{t('admin.delivery_vehicle_desc')}</p><Input value={(settings.delivery?.vehicle_skus || []).join(', ')} onChange={(e) => updateDelivery('vehicle_skus', e.target.value.split(',').map(s => s.trim()).filter(Boolean))} placeholder="cam20, cam12" className="rounded-none" data-testid="vehicle-skus" /></div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* UPLOADS */}
          <TabsContent value="uploads">
            <Card className="rounded-none">
              <CardHeader><CardTitle className="text-lg">{t('admin.uploads_title')}</CardTitle></CardHeader>
              <CardContent className="space-y-6">
                <p className="text-sm text-muted-foreground">{t('admin.uploads_desc')}</p>

                {/* Drop zone */}
                <div
                  className="border-2 border-dashed border-border hover:border-primary transition-colors p-8 text-center cursor-pointer"
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => { e.preventDefault(); const dt = new DataTransfer(); for (const f of e.dataTransfer.files) dt.items.add(f); fileInputRef.current.files = dt.files; handleFileUpload({ target: { files: e.dataTransfer.files } }); }}
                  data-testid="upload-dropzone"
                >
                  <input ref={fileInputRef} type="file" multiple accept="image/*" className="hidden" onChange={handleFileUpload} />
                  {uploading ? (
                    <div className="flex items-center justify-center gap-2"><RefreshCw className="w-5 h-5 animate-spin text-primary" /><span className="text-sm font-medium">{t('admin.uploads_uploading')}</span></div>
                  ) : (
                    <div><Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" /><p className="text-sm font-medium">{t('admin.uploads_drop')}</p><p className="text-xs text-muted-foreground mt-1">{t('admin.uploads_formats')}</p></div>
                  )}
                </div>

                <Separator />
                <h3 className="font-semibold text-sm">{t('admin.uploads_recent')}</h3>

                {uploads.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">{t('admin.uploads_empty')}</p>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                    {uploads.map((upload) => (
                      <div key={upload.filename} className="group border border-border overflow-hidden" data-testid={`upload-${upload.filename}`}>
                        <div className="aspect-square bg-gray-50 p-1">
                          <img src={upload.url} alt={upload.original_name || upload.filename} className="w-full h-full object-contain" loading="lazy" />
                        </div>
                        <div className="p-2 space-y-1">
                          <p className="text-[10px] truncate text-muted-foreground">{upload.original_name || upload.filename}</p>
                          <div className="flex gap-1">
                            <Button
                              variant="outline" size="sm"
                              className="rounded-none h-6 text-[10px] flex-1 gap-1"
                              onClick={() => copyUrl(upload.url)}
                              data-testid={`copy-${upload.filename}`}
                            >
                              {copiedUrl === upload.url ? <><Check className="w-3 h-3" /> {t('admin.uploads_copied')}</> : <><Copy className="w-3 h-3" /> {t('admin.uploads_copy')}</>}
                            </Button>
                            <Button
                              variant="ghost" size="sm"
                              className="rounded-none h-6 text-[10px] text-red-500 hover:text-red-700 px-1"
                              onClick={() => handleDeleteUpload(upload.filename)}
                              data-testid={`delete-${upload.filename}`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* POPUP */}
          <TabsContent value="popup">
            <Card className="rounded-none">
              <CardHeader><CardTitle className="text-lg">{t('admin.popup_title')}</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label>{t('admin.popup_enabled')}</Label>
                  <Switch checked={settings.popup?.enabled || false} onCheckedChange={(v) => updatePopup('enabled', v)} data-testid="popup-enabled-switch" />
                </div>
                <Separator />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('admin.popup_headline')}</Label>
                    <Input value={settings.popup?.title || ''} onChange={(e) => updatePopup('title', e.target.value)} className="rounded-none" data-testid="popup-title-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('admin.popup_image')}</Label>
                    <ImageUploader value={settings.popup?.image_url || ''} onChange={(url) => updatePopup('image_url', url)} testIdPrefix="popup-image" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>{t('admin.popup_message')}</Label>
                  <Textarea value={settings.popup?.message || ''} onChange={(e) => updatePopup('message', e.target.value)} className="rounded-none" rows={3} data-testid="popup-message-input" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('admin.popup_link')}</Label>
                    <Input value={settings.popup?.link_url || ''} onChange={(e) => updatePopup('link_url', e.target.value)} className="rounded-none" placeholder="/catalogue" data-testid="popup-link-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('admin.popup_link_text')}</Label>
                    <Input value={settings.popup?.link_text || ''} onChange={(e) => updatePopup('link_text', e.target.value)} className="rounded-none" data-testid="popup-link-text-input" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('admin.popup_display_mode')}</Label>
                    <Select value={settings.popup?.display_mode || 'once_per_session'} onValueChange={(v) => updatePopup('display_mode', v)}>
                      <SelectTrigger className="rounded-none" data-testid="popup-display-mode"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="once_per_session">{t('admin.popup_once_session')}</SelectItem>
                        <SelectItem value="once_per_day">{t('admin.popup_once_day')}</SelectItem>
                        <SelectItem value="always">{t('admin.popup_always')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('admin.popup_style')}</Label>
                    <Select value={settings.popup?.style || 'modal'} onValueChange={(v) => updatePopup('style', v)}>
                      <SelectTrigger className="rounded-none" data-testid="popup-style"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="modal">{t('admin.popup_style_modal')}</SelectItem>
                        <SelectItem value="banner">{t('admin.popup_style_banner')}</SelectItem>
                        <SelectItem value="slide">{t('admin.popup_style_slide')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Preview */}
                {settings.popup?.title && (
                  <>
                    <Separator />
                    <Label className="block mb-2">{t('admin.popup_preview')}</Label>
                    <div className="border border-border p-4 bg-gray-50">
                      {settings.popup?.style === 'banner' ? (
                        <div className="bg-primary text-white px-4 py-3 flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3">
                            {settings.popup?.image_url && <img src={settings.popup.image_url} alt="" className="w-8 h-8 rounded object-cover" />}
                            <div><p className="font-semibold text-sm">{settings.popup.title}</p>{settings.popup.message && <p className="text-xs opacity-90">{settings.popup.message}</p>}</div>
                          </div>
                          {settings.popup?.link_text && <span className="text-xs bg-white text-primary px-3 py-1 font-medium">{settings.popup.link_text}</span>}
                        </div>
                      ) : (
                        <div className="max-w-xs mx-auto bg-white border border-border shadow-lg">
                          {settings.popup?.image_url && <img src={settings.popup.image_url} alt="" className="w-full h-32 object-cover" />}
                          <div className="p-4">
                            <h3 className="font-bold text-sm mb-1">{settings.popup.title}</h3>
                            {settings.popup.message && <p className="text-xs text-muted-foreground mb-3">{settings.popup.message}</p>}
                            {settings.popup?.link_text && <div className="bg-primary text-white text-center text-xs py-2 font-medium">{settings.popup.link_text}</div>}
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}

                <Button onClick={() => saveSection('popup', settings.popup)} disabled={saving} className="rounded-none gap-2" data-testid="save-popup-btn">
                  <Save className="w-4 h-4" /> {saving ? t('admin.saving') : t('admin.popup_save')}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* WORDPRESS WELCOME CAMPAIGN */}
          <TabsContent value="campaign">
            <WelcomeCampaignPanel />
          </TabsContent>

          {/* WORDPRESS IMPORTS (users + historic orders) */}
          <TabsContent value="wp-import">
            <WpImportPanel />
          </TabsContent>

          {/* UNCATEGORIZED PRODUCTS AUDIT */}
          <TabsContent value="uncategorized">
            <UncategorizedProductsPanel />
          </TabsContent>

          {/* PRODUCTS WITHOUT IMAGE AUDIT */}
          <TabsContent value="without-image">
            <ProductsWithoutImagePanel />
          </TabsContent>

          {/* CUSTOMER PRODUCT REVIEWS */}
          <TabsContent value="reviews">
            <AdminReviewsPanel />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPage;
